import { createApiRef, createRouteRef, createPlugin, createApiFactory, createRoutableExtension, createComponentExtension, useApi, configApiRef, githubAuthApiRef } from '@backstage/core-plugin-api';
import { createCardExtension } from '@backstage/plugin-home-react';
import { Octokit } from '@octokit/rest';
import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route } from 'react-router';
import { makeStyles, Tooltip, Box, Typography, ButtonGroup, Button, TextField, InputAdornment, IconButton, Grid, CircularProgress, FormControl, Select, MenuItem, FormHelperText } from '@material-ui/core';
import { Table, MarkdownContent, MissingAnnotationEmptyState, Page, Content, ContentHeader, SupportButton, InfoCard, StructuredMetadataTable } from '@backstage/core-components';
import { debounce } from 'lodash';
import GitHubIcon from '@material-ui/icons/GitHub';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { useAsyncRetry, useAsync } from 'react-use';
import { DateTime, Duration } from 'luxon';
import { useEntity } from '@backstage/plugin-catalog-react';
import { makeStyles as makeStyles$1 } from '@material-ui/core/styles';

const githubPullRequestsApiRef = createApiRef({
  id: "plugin.githubpullrequests.service"
});

class GithubPullRequestsClient {
  async listPullRequests({
    search = "",
    token,
    owner,
    repo,
    pageSize = 5,
    page,
    baseUrl
  }) {
    const pullRequestResponse = await new Octokit({
      auth: token,
      ...baseUrl && { baseUrl }
    }).search.issuesAndPullRequests({
      q: `${search} in:title type:pr repo:${owner}/${repo}`,
      per_page: pageSize,
      page
    });
    return {
      pullRequestsData: pullRequestResponse.data
    };
  }
  async getRepositoryData({
    baseUrl,
    token,
    url
  }) {
    const response = await new Octokit({
      auth: token,
      ...baseUrl && { baseUrl }
    }).request({ url });
    return {
      htmlUrl: response.data.html_url,
      fullName: response.data.full_name,
      additions: response.data.additions,
      deletions: response.data.deletions,
      changedFiles: response.data.changed_files
    };
  }
}

const entityContentRouteRef = createRouteRef({
  id: "github-pull-requests"
});
const githubPullRequestsPlugin = createPlugin({
  id: "github-pull-requests",
  apis: [
    createApiFactory(githubPullRequestsApiRef, new GithubPullRequestsClient())
  ],
  routes: {
    entityContent: entityContentRouteRef
  }
});
const EntityGithubPullRequestsContent = githubPullRequestsPlugin.provide(
  createRoutableExtension({
    name: "EntityGithubPullRequestsContent",
    component: () => Promise.resolve().then(function () { return Router$1; }).then((m) => m.Router),
    mountPoint: entityContentRouteRef
  })
);
const EntityGithubPullRequestsOverviewCard = githubPullRequestsPlugin.provide(
  createComponentExtension({
    name: "EntityGithubPullRequestsOverviewCard",
    component: {
      lazy: () => import('./index-07c546cc.esm.js').then(
        (m) => m.PullRequestsStatsCard
      )
    }
  })
);
const EntityGithubPullRequestsTable = githubPullRequestsPlugin.provide(
  createComponentExtension({
    name: "EntityGithubPullRequestsTable",
    component: {
      lazy: () => import('./index-877bf71b.esm.js').then((m) => m.PullRequestsTable)
    }
  })
);
const HomePageRequestedReviewsCard = githubPullRequestsPlugin.provide(
  createCardExtension({
    name: "HomePageRequestedReviewsCard",
    title: "Review requests",
    components: () => import('./index-3fc8e4ee.esm.js')
  })
);
const HomePageYourOpenPullRequestsCard = githubPullRequestsPlugin.provide(
  createCardExtension({
    name: "YourOpenPullRequestsCard",
    title: "Your open pull requests",
    components: () => import('./index-b23626ba.esm.js')
  })
);
const EntityGithubGroupPullRequestsCard = githubPullRequestsPlugin.provide(
  createCardExtension({
    name: "EntityGithubGroupPullRequestsCard",
    title: "Team Assigned Review requests",
    components: () => import('./index-71821a50.esm.js')
  })
);

const GITHUB_PULL_REQUESTS_ANNOTATION = "github.com/project-slug";
const GITHUB_PULL_REQUESTS_TEAM_ANNOTATION = "github.com/team-slug";
const isGithubSlugSet = (entity) => {
  var _a, _b;
  return (_b = (_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[GITHUB_PULL_REQUESTS_ANNOTATION]) != null ? _b : "";
};
const isGithubTeamSlugSet = (entity) => {
  var _a, _b;
  return (_b = (_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[GITHUB_PULL_REQUESTS_TEAM_ANNOTATION]) != null ? _b : "";
};

const ROADIE_BACKSTAGE_PULL_REQUESTS_DEFAULT_FILTER_ANNOTATION = "roadie-backstage-pull-requests/default-filter";
const isRoadieBackstageDefaultFilterSet = (entity) => {
  var _a, _b;
  return (_b = (_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[ROADIE_BACKSTAGE_PULL_REQUESTS_DEFAULT_FILTER_ANNOTATION]) != null ? _b : "";
};

const useBaseUrl = () => {
  var _a;
  const config = useApi(configApiRef);
  const providerConfigs = (_a = config.getOptionalConfigArray("integrations.github")) != null ? _a : [];
  const targetProviderConfig = providerConfigs[0];
  const baseUrl = targetProviderConfig == null ? void 0 : targetProviderConfig.getOptionalString("apiBaseUrl");
  return baseUrl;
};

function usePullRequests({
  search,
  owner,
  repo,
  branch
}) {
  const api = useApi(githubPullRequestsApiRef);
  const auth = useApi(githubAuthApiRef);
  const baseUrl = useBaseUrl();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const getElapsedTime = (start) => {
    return DateTime.fromISO(start).toRelative();
  };
  const {
    loading,
    value: prData,
    retry,
    error
  } = useAsyncRetry(async () => {
    const token = await auth.getAccessToken(["repo"]);
    if (!repo) {
      return [];
    }
    return api.listPullRequests({
      token,
      search,
      owner,
      repo,
      pageSize,
      page: page + 1,
      branch,
      baseUrl
    }).then(
      ({
        pullRequestsData: { total_count, items }
      }) => {
        if (total_count >= 0) {
          setTotal(total_count);
        }
        return items.map(
          ({
            id,
            html_url,
            title,
            number,
            created_at,
            updated_at,
            user,
            state: pr_state,
            draft,
            pull_request: { merged_at },
            body
          }) => ({
            url: html_url,
            id,
            number,
            title,
            body,
            state: pr_state,
            draft,
            merged: merged_at,
            creatorNickname: user == null ? void 0 : user.login,
            creatorProfileLink: user == null ? void 0 : user.html_url,
            createdTime: getElapsedTime(created_at),
            updatedTime: getElapsedTime(updated_at)
          })
        );
      }
    );
  }, [page, pageSize, repo, owner]);
  useEffect(() => {
    setPage(0);
    retry();
  }, [search]);
  return [
    {
      page,
      pageSize,
      loading,
      prData,
      projectName: `${owner}/${repo}`,
      total,
      error
    },
    {
      prData,
      setPage,
      setPageSize,
      retry
    }
  ];
}

const useStyles$1 = makeStyles(() => ({
  open: {
    fill: "#22863a",
    verticalAlign: "sub"
  },
  closed: {
    fill: "#cb2431",
    verticalAlign: "sub"
  },
  merged: {
    fill: "#6f42c1",
    verticalAlign: "sub"
  },
  draft: {
    fill: "#6a737d",
    verticalAlign: "sub"
  },
  comment: {
    fill: "#768390",
    verticalAlign: "sub"
  }
}));
const StatusOpen = () => {
  const classes = useStyles$1();
  return /* @__PURE__ */ React.createElement("svg", { width: "16", height: "16", className: classes.open, viewBox: "0 0 16 16" }, /* @__PURE__ */ React.createElement(
    "path",
    {
      fillRule: "evenodd",
      d: "M7.177 3.073L9.573.677A.25.25 0 0110 .854v4.792a.25.25 0 01-.427.177L7.177 3.427a.25.25 0 010-.354zM3.75 2.5a.75.75 0 100 1.5.75.75 0 000-1.5zm-2.25.75a2.25 2.25 0 113 2.122v5.256a2.251 2.251 0 11-1.5 0V5.372A2.25 2.25 0 011.5 3.25zM11 2.5h-1V4h1a1 1 0 011 1v5.628a2.251 2.251 0 101.5 0V5A2.5 2.5 0 0011 2.5zm1 10.25a.75.75 0 111.5 0 .75.75 0 01-1.5 0zM3.75 12a.75.75 0 100 1.5.75.75 0 000-1.5z"
    }
  ));
};
const StatusClosed = () => {
  const classes = useStyles$1();
  return /* @__PURE__ */ React.createElement("svg", { width: "16", height: "16", className: classes.closed, viewBox: "0 0 16 16" }, /* @__PURE__ */ React.createElement(
    "path",
    {
      fillRule: "evenodd",
      d: "M7.177 3.073L9.573.677A.25.25 0 0110 .854v4.792a.25.25 0 01-.427.177L7.177 3.427a.25.25 0 010-.354zM3.75 2.5a.75.75 0 100 1.5.75.75 0 000-1.5zm-2.25.75a2.25 2.25 0 113 2.122v5.256a2.251 2.251 0 11-1.5 0V5.372A2.25 2.25 0 011.5 3.25zM11 2.5h-1V4h1a1 1 0 011 1v5.628a2.251 2.251 0 101.5 0V5A2.5 2.5 0 0011 2.5zm1 10.25a.75.75 0 111.5 0 .75.75 0 01-1.5 0zM3.75 12a.75.75 0 100 1.5.75.75 0 000-1.5z"
    }
  ));
};
const StatusMerged = () => {
  const classes = useStyles$1();
  return /* @__PURE__ */ React.createElement("svg", { width: "16", height: "16", className: classes.merged, viewBox: "0 0 16 16" }, /* @__PURE__ */ React.createElement(
    "path",
    {
      fillRule: "evenodd",
      d: "M5 3.254V3.25v.005a.75.75 0 110-.005v.004zm.45 1.9a2.25 2.25 0 10-1.95.218v5.256a2.25 2.25 0 101.5 0V7.123A5.735 5.735 0 009.25 9h1.378a2.251 2.251 0 100-1.5H9.25a4.25 4.25 0 01-3.8-2.346zM12.75 9a.75.75 0 100-1.5.75.75 0 000 1.5zm-8.5 4.5a.75.75 0 100-1.5.75.75 0 000 1.5z"
    }
  ));
};
const StatusDraft = () => {
  const classes = useStyles$1();
  return /* @__PURE__ */ React.createElement("svg", { width: "16", height: "16", className: classes.draft, viewBox: "0 0 16 16" }, /* @__PURE__ */ React.createElement(
    "path",
    {
      fillRule: "evenodd",
      d: "M7.177 3.073L9.573.677A.25.25 0 0110 .854v4.792a.25.25 0 01-.427.177L7.177 3.427a.25.25 0 010-.354zM3.75 2.5a.75.75 0 100 1.5.75.75 0 000-1.5zm-2.25.75a2.25 2.25 0 113 2.122v5.256a2.251 2.251 0 11-1.5 0V5.372A2.25 2.25 0 011.5 3.25zM11 2.5h-1V4h1a1 1 0 011 1v5.628a2.251 2.251 0 101.5 0V5A2.5 2.5 0 0011 2.5zm1 10.25a.75.75 0 111.5 0 .75.75 0 01-1.5 0zM3.75 12a.75.75 0 100 1.5.75.75 0 000-1.5z"
    }
  ));
};
const CommentIcon = () => {
  const classes = useStyles$1();
  return /* @__PURE__ */ React.createElement("svg", { width: "16", height: "16", className: classes.comment, viewBox: "0 0 16 16" }, /* @__PURE__ */ React.createElement(
    "path",
    {
      fillRule: "evenodd",
      d: "M2.75 2.5a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h2a.75.75 0 01.75.75v2.19l2.72-2.72a.75.75 0 01.53-.22h4.5a.25.25 0 00.25-.25v-7.5a.25.25 0 00-.25-.25H2.75zM1 2.75C1 1.784 1.784 1 2.75 1h10.5c.966 0 1.75.784 1.75 1.75v7.5A1.75 1.75 0 0113.25 12H9.06l-2.573 2.573A1.457 1.457 0 014 13.543V12H2.75A1.75 1.75 0 011 10.25v-7.5z"
    }
  ));
};
const getStatusIconType = (row) => {
  switch (true) {
    case (row.state === "open" && row.draft):
      return /* @__PURE__ */ React.createElement(Tooltip, { title: "Draft" }, /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(StatusDraft, null)));
    case row.state === "open":
      return /* @__PURE__ */ React.createElement(Tooltip, { title: "Open" }, /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(StatusOpen, null)));
    case (row.state === "closed" && Boolean(row.merged)):
      return /* @__PURE__ */ React.createElement(Tooltip, { title: "Merged" }, /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(StatusMerged, null)));
    case row.state === "closed":
      return /* @__PURE__ */ React.createElement(Tooltip, { title: "Closed" }, /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(StatusClosed, null)));
    default:
      return null;
  }
};

const generatedColumns = [
  {
    title: "ID",
    field: "number",
    highlight: true,
    width: "150px",
    render: (row) => /* @__PURE__ */ React.createElement(Box, { fontWeight: "fontWeightBold" }, /* @__PURE__ */ React.createElement("a", { target: "_blank", rel: "noopener noreferrer", href: row.url }, "#", row.number))
  },
  {
    title: "Title",
    field: "title",
    highlight: true,
    render: (row) => /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, getStatusIconType(row), " ", /* @__PURE__ */ React.createElement(Box, { ml: 1, component: "span" }, row.title))
  },
  {
    title: "Creator",
    field: "creatorNickname",
    highlight: true,
    render: (row) => /* @__PURE__ */ React.createElement(Box, { fontWeight: "fontWeightBold" }, /* @__PURE__ */ React.createElement(
      "a",
      {
        target: "_blank",
        rel: "noopener noreferrer",
        href: row.creatorProfileLink
      },
      row.creatorNickname
    ))
  },
  {
    title: "Created",
    field: "createdTime",
    highlight: true,
    render: (row) => /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, row.createdTime)
  },
  {
    title: "Last updated",
    field: "updatedTime",
    highlight: true,
    render: (row) => /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, row.updatedTime)
  }
];
const PullRequestsTableView = ({
  projectName,
  loading,
  pageSize,
  page,
  prData,
  onChangePage,
  onChangePageSize,
  total,
  StateFilterComponent,
  SearchComponent
}) => {
  return /* @__PURE__ */ React.createElement(
    Table,
    {
      detailPanel: ({ rowData }) => {
        var _a;
        return /* @__PURE__ */ React.createElement(Box, { marginLeft: "14px" }, /* @__PURE__ */ React.createElement(
          MarkdownContent,
          {
            content: (_a = rowData.body) != null ? _a : "_No description provided._",
            dialect: "gfm"
          }
        ));
      },
      isLoading: loading,
      options: { paging: true, search: false, pageSize, padding: "dense" },
      totalCount: total,
      page,
      actions: [],
      data: prData != null ? prData : [],
      onPageChange: onChangePage,
      onRowsPerPageChange: onChangePageSize,
      title: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        Box,
        {
          position: "absolute",
          left: "20px",
          top: "17px",
          width: "45%",
          display: "flex",
          alignItems: "center"
        },
        /* @__PURE__ */ React.createElement(GitHubIcon, null),
        /* @__PURE__ */ React.createElement(Box, { mr: 1 }),
        /* @__PURE__ */ React.createElement(Typography, { variant: "h6", noWrap: true }, projectName)
      ), /* @__PURE__ */ React.createElement(
        Box,
        {
          position: "absolute",
          right: "20px",
          top: "15px",
          width: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end"
        },
        StateFilterComponent ? /* @__PURE__ */ React.createElement(StateFilterComponent, null) : /* @__PURE__ */ React.createElement(React.Fragment, null),
        StateFilterComponent && SearchComponent ? /* @__PURE__ */ React.createElement(Box, { mr: 1 }) : /* @__PURE__ */ React.createElement(React.Fragment, null),
        SearchComponent ? /* @__PURE__ */ React.createElement(SearchComponent, null) : /* @__PURE__ */ React.createElement(React.Fragment, null)
      )),
      columns: generatedColumns
    }
  );
};
const PullRequests = (__props) => {
  const { entity } = useEntity();
  const projectName = isGithubSlugSet(entity);
  const defaultFilter = isRoadieBackstageDefaultFilterSet(entity);
  const [owner, repo] = (projectName != null ? projectName : "/").split("/");
  const [search, setSearch] = useState(`state:open ${defaultFilter}`);
  const setSearchValueDebounced = useRef(debounce(setSearch, 500));
  const onChangePRStatusFilter = (state) => {
    if (state === "all") {
      setSearch(
        (currentSearch) => currentSearch.replace(/state:(open|closed)/g, "").trim()
      );
    } else {
      setSearch(
        (currentSearch) => currentSearch.search(/state:(open|closed)/g) === -1 ? `state:${state} ${currentSearch}` : currentSearch.replace(/state:(open|closed)/g, `state:${state}`)
      );
    }
  };
  const [tableProps, { retry, setPage, setPageSize }] = usePullRequests({
    search,
    owner,
    repo
  });
  const StateFilterComponent = () => /* @__PURE__ */ React.createElement(Box, null, /* @__PURE__ */ React.createElement(ButtonGroup, { color: "primary", "aria-label": "text primary button group" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      color: search.search("state:open") !== -1 ? "primary" : "default",
      onClick: () => onChangePRStatusFilter("open")
    },
    "OPEN"
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      color: search.search("state:closed") !== -1 ? "primary" : "default",
      onClick: () => onChangePRStatusFilter("closed")
    },
    "CLOSED"
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      color: search.search("state") === -1 ? "primary" : "default",
      onClick: () => onChangePRStatusFilter("all")
    },
    "ALL"
  )));
  const SearchComponent = () => {
    const [draftSearch, setDraftSearch] = useState(search);
    return /* @__PURE__ */ React.createElement(Box, { width: 500 }, /* @__PURE__ */ React.createElement(
      TextField,
      {
        fullWidth: true,
        onChange: (event) => {
          setDraftSearch(event.target.value);
          setSearchValueDebounced.current(event.target.value);
        },
        placeholder: "Filter",
        value: draftSearch,
        InputProps: {
          startAdornment: /* @__PURE__ */ React.createElement(InputAdornment, { position: "start" }, /* @__PURE__ */ React.createElement(SearchIcon, { fontSize: "small" })),
          endAdornment: /* @__PURE__ */ React.createElement(InputAdornment, { position: "end" }, /* @__PURE__ */ React.createElement(
            IconButton,
            {
              disabled: !draftSearch,
              onClick: () => {
                setDraftSearch("");
                setSearch("");
              }
            },
            /* @__PURE__ */ React.createElement(ClearIcon, { fontSize: "small", "aria-label": "clear" })
          ))
        }
      }
    ));
  };
  return /* @__PURE__ */ React.createElement(
    PullRequestsTableView,
    {
      ...tableProps,
      StateFilterComponent,
      SearchComponent,
      loading: tableProps.loading,
      retry,
      onChangePageSize: setPageSize,
      onChangePage: setPage
    }
  );
};
const PullRequestsTable = (__props) => {
  const { entity } = useEntity();
  const projectName = isGithubSlugSet(entity);
  if (!projectName || projectName === "") {
    return /* @__PURE__ */ React.createElement(
      MissingAnnotationEmptyState,
      {
        annotation: GITHUB_PULL_REQUESTS_ANNOTATION
      }
    );
  }
  return /* @__PURE__ */ React.createElement(PullRequests, null);
};

const PullRequestsPage = () => /* @__PURE__ */ React.createElement(Page, { themeId: "tool" }, /* @__PURE__ */ React.createElement(Content, null, /* @__PURE__ */ React.createElement(ContentHeader, { title: "GitHub Pull Requests" }, /* @__PURE__ */ React.createElement(SupportButton, null, "Plugin to show a project's pull requests on GitHub")), /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 3, direction: "column" }, /* @__PURE__ */ React.createElement(Grid, { item: true }, /* @__PURE__ */ React.createElement(PullRequestsTable, null)))));

const isGithubPullRequestsAvailable = (entity) => {
  var _a;
  return Boolean((_a = entity.metadata.annotations) == null ? void 0 : _a[GITHUB_PULL_REQUESTS_ANNOTATION]);
};
const isGithubTeamPullRequestsAvailable = (entity) => {
  var _a;
  return Boolean((_a = entity.metadata.annotations) == null ? void 0 : _a[GITHUB_PULL_REQUESTS_TEAM_ANNOTATION]);
};
const Router = () => {
  const { entity } = useEntity();
  return !isGithubPullRequestsAvailable(entity) ? /* @__PURE__ */ React.createElement(MissingAnnotationEmptyState, { annotation: GITHUB_PULL_REQUESTS_ANNOTATION }) : /* @__PURE__ */ React.createElement(Routes, null, /* @__PURE__ */ React.createElement(Route, { path: "/", element: /* @__PURE__ */ React.createElement(PullRequestsPage, null) }));
};

var Router$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  isGithubPullRequestsAvailable: isGithubPullRequestsAvailable,
  isGithubTeamPullRequestsAvailable: isGithubTeamPullRequestsAvailable,
  Router: Router
});

function calculateStatistics(pullRequestsData) {
  const result = pullRequestsData.reduce(
    (acc, curr) => {
      acc.avgTimeUntilMerge += curr.pullRequest.mergedAt ? new Date(curr.pullRequest.mergedAt).getTime() - new Date(curr.createdAt).getTime() : 0;
      acc.mergedCount += curr.pullRequest.mergedAt ? 1 : 0;
      acc.closedCount += curr.closedAt ? 1 : 0;
      acc.additions += curr.pullRequest.additions;
      acc.deletions += curr.pullRequest.deletions;
      acc.changedLinesCount += curr.pullRequest.additions + curr.pullRequest.deletions;
      acc.changedFilesCount += curr.pullRequest.changedFiles;
      return acc;
    },
    {
      avgTimeUntilMerge: 0,
      closedCount: 0,
      mergedCount: 0,
      changedLinesCount: 0,
      changedFilesCount: 0,
      additions: 0,
      deletions: 0
    }
  );
  return {
    ...result,
    avgChangedLinesCount: Math.round(
      result.changedLinesCount / pullRequestsData.length
    ),
    avgChangedFilesCount: Math.round(
      result.changedFilesCount / pullRequestsData.length
    ),
    avgAdditions: Math.round(result.additions / pullRequestsData.length),
    avgDeletions: Math.round(result.deletions / pullRequestsData.length)
  };
}
function usePullRequestsStatistics({
  owner,
  repo,
  branch,
  pageSize,
  state
}) {
  const api = useApi(githubPullRequestsApiRef);
  const auth = useApi(githubAuthApiRef);
  const baseUrl = useBaseUrl();
  const {
    loading,
    value: statsData,
    error
  } = useAsync(async () => {
    const token = await auth.getAccessToken(["repo"]);
    if (!repo) {
      return {
        avgTimeUntilMerge: "Never",
        mergedToClosedRatio: "0%",
        avgChangedLinesCount: 0,
        avgChangedFilesCount: 0,
        avgAdditions: 0,
        avgDeletions: 0
      };
    }
    const {
      pullRequestsData
    } = await api.listPullRequests({
      search: `state:${state}`,
      token,
      owner,
      repo,
      pageSize,
      page: 1,
      branch,
      baseUrl
    });
    const transformedData = await Promise.all(
      pullRequestsData.items.map(async (pr) => {
        const repoData = await api.getRepositoryData({
          url: pr.pull_request.url,
          baseUrl,
          token
        });
        return {
          createdAt: pr.created_at,
          closedAt: pr.closed_at,
          pullRequest: {
            mergedAt: pr.pull_request.merged_at,
            additions: repoData.additions,
            deletions: repoData.deletions,
            changedFiles: repoData.changedFiles
          }
        };
      })
    );
    const calcResult = calculateStatistics(transformedData);
    if (calcResult.closedCount === 0 || calcResult.mergedCount === 0)
      return {
        ...calcResult,
        avgTimeUntilMerge: "Never",
        mergedToClosedRatio: "0%"
      };
    const avgTimeUntilMergeDiff = calcResult.avgTimeUntilMerge / calcResult.mergedCount;
    const avgTimeUntilMerge = Duration.fromMillis(avgTimeUntilMergeDiff).shiftTo("months", "days", "hour").toHuman({ notation: "compact" });
    return {
      ...calcResult,
      avgTimeUntilMerge,
      mergedToClosedRatio: `${Math.round(
        calcResult.mergedCount / calcResult.closedCount * 100
      )}%`
    };
  }, [pageSize, repo, owner]);
  return [
    {
      loading,
      statsData,
      projectName: `${owner}/${repo}`,
      error
    }
  ];
}

const useTooltipContentStyles = makeStyles$1((theme) => ({
  container: {
    backgroundColor: theme.palette.background.paper,
    fontSize: "1rem",
    fontWeight: Number(theme.typography.fontWeightBold),
    borderRadius: "6px",
    boxShadow: theme.shadows[10]
  },
  additions: {
    color: theme.palette.success.main
  },
  deletions: {
    color: theme.palette.error.main
  }
}));
const TooltipContent = (props) => {
  const { additions, deletions } = props;
  const classes = useTooltipContentStyles();
  return /* @__PURE__ */ React.createElement(Grid, { container: true, className: classes.container }, /* @__PURE__ */ React.createElement(Grid, { item: true, className: classes.additions }, "+", additions), /* @__PURE__ */ React.createElement(Grid, { item: true, className: classes.deletions }, "-", deletions));
};

const useStyles = makeStyles((theme) => ({
  infoCard: {
    marginBottom: theme.spacing(3),
    "& + .MuiAlert-root": {
      marginTop: theme.spacing(3)
    },
    "& .MuiCardContent-root": {
      padding: theme.spacing(2, 1, 2, 2)
    },
    "& td": {
      whiteSpace: "normal"
    }
  }
}));
const StatsCard = (props) => {
  const { entity } = useEntity();
  const classes = useStyles();
  const [pageSize, setPageSize] = useState(20);
  const projectName = isGithubSlugSet(entity);
  const [owner, repo] = (projectName != null ? projectName : "/").split("/");
  const [{ statsData, loading: loadingStatistics }] = usePullRequestsStatistics(
    {
      owner,
      repo,
      pageSize,
      state: "closed"
    }
  );
  const metadata = {
    "average time of PR until merge": statsData == null ? void 0 : statsData.avgTimeUntilMerge,
    "merged to closed ratio": statsData == null ? void 0 : statsData.mergedToClosedRatio,
    "average size of PR": /* @__PURE__ */ React.createElement(
      Tooltip,
      {
        title: /* @__PURE__ */ React.createElement(
          TooltipContent,
          {
            additions: statsData == null ? void 0 : statsData.avgAdditions,
            deletions: statsData == null ? void 0 : statsData.avgDeletions
          }
        )
      },
      /* @__PURE__ */ React.createElement("div", null, statsData == null ? void 0 : statsData.avgChangedLinesCount, " lines")
    ),
    "average changed files of PR": `${statsData == null ? void 0 : statsData.avgChangedFilesCount}`
  };
  return /* @__PURE__ */ React.createElement(
    InfoCard,
    {
      title: "GitHub Pull Requests Statistics",
      className: classes.infoCard,
      variant: props.variant
    },
    loadingStatistics ? /* @__PURE__ */ React.createElement(CircularProgress, null) : /* @__PURE__ */ React.createElement(Box, { position: "relative" }, /* @__PURE__ */ React.createElement(StructuredMetadataTable, { metadata }), /* @__PURE__ */ React.createElement(Box, { display: "flex", justifyContent: "flex-end" }, /* @__PURE__ */ React.createElement(FormControl, null, /* @__PURE__ */ React.createElement(
      Select,
      {
        value: pageSize,
        onChange: (event) => setPageSize(Number(event.target.value))
      },
      /* @__PURE__ */ React.createElement(MenuItem, { value: 10 }, "10"),
      /* @__PURE__ */ React.createElement(MenuItem, { value: 20 }, "20"),
      /* @__PURE__ */ React.createElement(MenuItem, { value: 50 }, "50"),
      /* @__PURE__ */ React.createElement(MenuItem, { value: 100 }, "100")
    ), /* @__PURE__ */ React.createElement(FormHelperText, null, "Number of PRs"))))
  );
};
const PullRequestsStatsCard = (props) => {
  const { entity } = useEntity();
  const projectName = isGithubSlugSet(entity);
  if (!projectName || projectName === "") {
    return /* @__PURE__ */ React.createElement(
      MissingAnnotationEmptyState,
      {
        annotation: GITHUB_PULL_REQUESTS_ANNOTATION
      }
    );
  }
  return /* @__PURE__ */ React.createElement(StatsCard, { ...props });
};
var PullRequestsStatsCard$1 = PullRequestsStatsCard;

export { CommentIcon as C, EntityGithubPullRequestsContent as E, GithubPullRequestsClient as G, HomePageRequestedReviewsCard as H, PullRequestsStatsCard$1 as P, Router as R, PullRequestsTable as a, PullRequestsTableView as b, GITHUB_PULL_REQUESTS_TEAM_ANNOTATION as c, githubPullRequestsPlugin as d, EntityGithubPullRequestsOverviewCard as e, EntityGithubPullRequestsTable as f, getStatusIconType as g, EntityGithubGroupPullRequestsCard as h, isGithubTeamSlugSet as i, HomePageYourOpenPullRequestsCard as j, isGithubPullRequestsAvailable as k, isGithubTeamPullRequestsAvailable as l, githubPullRequestsApiRef as m, useBaseUrl as u };
//# sourceMappingURL=index-1060b437.esm.js.map
